import { useDispatch, useSelector } from 'react-redux'
import { useCallback, useMemo, useState } from 'react'
import { updateVisitAsync } from '../actions/visits/visits.actions'
import { postBoardingAddOnsAsync } from '../actions/addons/addons.actions'
import moment from 'moment'
import useSelectedFacility from './useSelectedFacility'
import { CUTOFFS } from '../utils'

const BOARDING_HOLIDAY_TYPES = ['total', 'overnight', 'master', 'boarding']

const useBoardingVisit = () => {
	const dispatch = useDispatch()
	const loading = useSelector((state) => state.specific.visits.loading)
	const { facility } = useSelectedFacility()
	const [updating, setUpdating] = useState(false)

	const boarding = useMemo(() => {
		if (!facility || !facility.services?.boarding) {
			return null
		}
		const {
			services: { boarding: boardingServices }
		} = facility

		return boardingServices
	}, [facility])

	/**
	 * Boarding holidays for the current facility from 1 month past and forward
	 */
	const boardingHolidays = useMemo(() => {
		if (!facility) {
			return []
		}
		const { holidays } = facility || []

		const filtered = holidays.filter(({ type, startDate, active }) => {
			return (
				BOARDING_HOLIDAY_TYPES.includes(type) &&
				active &&
				moment(startDate).isSameOrAfter(moment().startOf('month').subtract(1, 'month'))
			)
		})

		return filtered
	}, [facility])

	const isABoardingHoliday = useCallback(
		(date) => {
			const boardingHoliday = boardingHolidays.find((holiday) => {
				const { startDate, endDate } = holiday

				const start = moment(startDate).startOf('day')
				const end = moment(endDate).endOf('day')
				const day = moment(date)

				const dayIsBewteen = day.isBetween(start, end)
				return dayIsBewteen
			})

			return boardingHoliday || null
		},
		[boardingHolidays]
	)

	const getCancellationPolicy = useCallback(
		(numPets) => {
			if (!boarding) {
				return 'Error getting cancellation policy'
			}

			if (!boarding.charge_cancellation_fee) {
				return `${boarding.no_fee_text}`
			}

			const end_time = moment(boarding.free_period_end_time, 'HH:mm').format('h:mm a')
			const end_date = CUTOFFS[boarding.free_period_end_date] || ''
			const mid_time = moment(boarding.mid_period_end_time, 'HH:mm').format('h:mm a')
			const mid_date = CUTOFFS[boarding.mid_period_end_date] || ''

			const extra_dogs = numPets - 1

			const fee_one = (
				(boarding.mid_period_base_fee + (boarding.mid_period_extra_dog_fee || 0) * extra_dogs) /
				100
			).toFixed(2)

			const full_fee_one_dog = `$${(boarding.final_period_base_fee / 100).toFixed(2)}`
			const full_fee_extra_dog = `$${(boarding.final_period_extra_dog_fee / 100).toFixed(2)}`
			return `If you need to cancel for any reason, you will not be charged if you cancel before ${end_time} ${end_date} the appointment. Cancellations made after ${end_time} ${end_date} the appointment but before ${mid_time} ${mid_date} the appointment will result in an $${fee_one} fee. Cancellations made after ${mid_time} ${mid_date} the appointment or no-shows will result in a fee of ${full_fee_one_dog} for the first dog plus ${full_fee_extra_dog} for each additional dog on the appointment.`
		},
		[boarding]
	)

	const prices = useMemo(() => {
		if (!boarding) {
			return null
		}

		return boarding?.prices || null
	}, [boarding])

	const update = useCallback(
		async ({ visitId, updates = {} }) => {
			const body = {
				visit_id: visitId,
				updates
			}
			setUpdating(true)
			const res = await updateVisitAsync(dispatch, body)
			setUpdating(false)
			return res
		},
		[dispatch]
	)

	const updateAddOns = useCallback(
		async ({ visitId, addons = [], petIds = [] }) => {
			if (!visitId) {
				dispatch({ type: 'UPDATE_VISIT_FAIL' })
				return
			}

			const addOnsForPayload = {}

			for (const petId of petIds) {
				addOnsForPayload[petId] = []
			}

			addons.forEach((addon) => {
				const {
					_id: rawId,
					addon_id: existingAddOnId,
					category,
					type,
					name,
					addon_choice_id,
					pet_id,
					time,
					date,
					training_service = false
				} = addon

				const record = {
					category,
					pet_id,
					date,
					training_service
				}

				if (existingAddOnId || rawId) {
					record['addon_id'] = existingAddOnId || rawId
				}

				if (name) {
					record['name'] = name
				}

				if (time) {
					record['time'] = time
				}

				if (type) {
					record['type'] = type
				}

				if (addon_choice_id) {
					record['addon_choice_id'] = addon_choice_id
				}

				if (addOnsForPayload[pet_id]) {
					addOnsForPayload[pet_id].push(record)
				}
			})

			setUpdating(true)
			await postBoardingAddOnsAsync(dispatch, visitId, addOnsForPayload)
			setUpdating(false)
		},
		[dispatch]
	)

	const standardBoardingDropoffHours = useMemo(() => {
		let text = 'between 5-9pm'
		if (!boarding) {
			return text
		}
		const { dropoff_begins, dropoff_deadline } = boarding

		text = `between ${
			moment(dropoff_begins, 'h:mm').format('ha') + ' - ' + moment(dropoff_deadline, 'h:mm').format('ha')
		}`
		return text
	}, [boarding])

	const standardBoardingPickupHours = useMemo(() => {
		let text = 'by 9am'
		if (!boarding) {
			return text
		}
		const { pickup_deadline } = boarding
		text = `by ${moment(pickup_deadline, 'h:mm').format('ha')}`

		return text
	}, [boarding])

	return {
		loading,
		update,
		updateAddOns,
		facility,
		getCancellationPolicy,
		prices,
		boarding,
		updating,
		isABoardingHoliday,
		boardingHolidays,
		standardBoardingDropoffHours,
		standardBoardingPickupHours
	}
}

export default useBoardingVisit
